import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AuthorizedButton } from '@/components/ui/AuthorizedButton'
import { usePermissions } from '@/hooks/use-permissions'
import { Input } from '@/components/ui/input'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Loader2, Trash2, Zap, AlertCircle } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { formatDateTime, formatPower } from '@/lib/utils'
import { useIncognito } from '@/hooks/use-incognito'
import { useControlSignals, useDeleteSignal, useClearAllSignals } from '@/hooks/use-control'

interface ScheduledTask {
  taskID: string
  poolID: string
  powerTarget: number
  startTime: string
  endTime: string
  nextExecution: string
}

interface SignalTimelineProps {
  poolID: string
  poolName: string
  onSignalsUpdated?: () => void
}

// --- Chart constants ---
const CW = 800
const CH = 200
const PAD = { top: 16, right: 24, bottom: 52, left: 76 }
const PW = CW - PAD.left - PAD.right
const PH = CH - PAD.top - PAD.bottom

function formatTickPower(w: number): string {
  const abs = Math.abs(w)
  if (abs >= 1_000_000) return `${(w / 1_000_000).toFixed(1)}MW`
  if (abs >= 1_000) return `${(w / 1_000).toFixed(1)}kW`
  return `${Math.round(w)}W`
}

function formatTickTime(ms: number, spanMs: number): string {
  const d = new Date(ms)
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  if (spanMs > 86_400_000) {
    const date = d.toLocaleDateString([], { month: 'short', day: 'numeric' })
    return `${time} ${date}`
  }
  return time
}

function TaskChart({
  tasks,
  selectedTask,
  onSelectTask,
}: {
  tasks: ScheduledTask[]
  selectedTask: string | null
  onSelectTask: (id: string | null) => void
}) {
  const times = tasks.flatMap((t) => [Date.parse(t.startTime), Date.parse(t.endTime)])
  const tMin = Math.min(...times)
  const tMax = Math.max(...times)
  const tSpan = tMax - tMin || 60_000

  const powers = tasks.map((t) => t.powerTarget)
  const rawMax = Math.max(0, ...powers)
  const rawMin = Math.min(0, ...powers)
  const pPad = (rawMax - rawMin) * 0.15 || Math.max(Math.abs(rawMax), 100) * 0.2
  const pHigh = rawMax + pPad
  const pLow = rawMin - pPad
  const pSpan = pHigh - pLow

  const toX = (ms: number) => PAD.left + ((ms - tMin) / tSpan) * PW
  const toY = (p: number) => PAD.top + ((pHigh - p) / pSpan) * PH
  const zeroY = toY(0)

  // X-axis ticks
  const numXTicks = 6
  const xTicks = Array.from({ length: numXTicks + 1 }, (_, i) => ({
    ms: tMin + (i / numXTicks) * tSpan,
    x: PAD.left + (i / numXTicks) * PW,
  }))

  // Y-axis ticks — pick clean round numbers
  const numYTicks = 5
  const yTicks = Array.from({ length: numYTicks }, (_, i) => {
    const p = pLow + (i / (numYTicks - 1)) * pSpan
    return { p, y: toY(p) }
  })

  const now = Date.now()
  const showNow = now > tMin && now < tMax
  const nowX = toX(now)

  return (
    <svg
      viewBox={`0 0 ${CW} ${CH}`}
      className="w-full"
      style={{ height: 'auto' }}
      aria-label="Control tasks timeline"
    >
      {/* Plot area background */}
      <rect
        x={PAD.left}
        y={PAD.top}
        width={PW}
        height={PH}
        className="fill-muted/30"
      />

      {/* Horizontal grid lines */}
      {yTicks.map(({ y }, i) => (
        <line
          key={i}
          x1={PAD.left}
          x2={PAD.left + PW}
          y1={y}
          y2={y}
          stroke="currentColor"
          className="text-muted-foreground/20"
          strokeWidth={1}
        />
      ))}

      {/* Zero line (thicker, only when range crosses zero) */}
      {pLow < 0 && pHigh > 0 && (
        <line
          x1={PAD.left}
          x2={PAD.left + PW}
          y1={zeroY}
          y2={zeroY}
          stroke="currentColor"
          className="text-muted-foreground/60"
          strokeWidth={1.5}
        />
      )}

      {/* Task bars */}
      {tasks.map((task) => {
        const x = toX(Date.parse(task.startTime))
        const barW = Math.max(2, toX(Date.parse(task.endTime)) - x)
        const isNeg = task.powerTarget < 0
        const barY = isNeg ? zeroY : toY(task.powerTarget)
        const barH = Math.max(2, Math.abs(toY(task.powerTarget) - zeroY))
        const isSelected = selectedTask === task.taskID

        return (
          <g
            key={task.taskID}
            className="cursor-pointer"
            onClick={() => onSelectTask(isSelected ? null : task.taskID)}
          >
            <rect
              x={x}
              y={barY}
              width={barW}
              height={barH}
              fill={isNeg ? 'hsl(var(--destructive))' : 'hsl(var(--primary))'}
              opacity={isSelected ? 0.95 : 0.65}
              rx={2}
            />
            {/* Selection highlight border */}
            {isSelected && (
              <rect
                x={x}
                y={barY}
                width={barW}
                height={barH}
                fill="none"
                stroke="hsl(var(--foreground))"
                strokeWidth={1.5}
                rx={2}
              />
            )}
            {/* Power label inside bar if wide enough */}
            {barW > 48 && barH > 14 && (
              <text
                x={x + barW / 2}
                y={barY + barH / 2 + 4}
                textAnchor="middle"
                fontSize={11}
                fill="hsl(var(--primary-foreground))"
                style={{ pointerEvents: 'none', fontWeight: 600 }}
              >
                {formatTickPower(task.powerTarget)}
              </text>
            )}
            <title>{`${formatPower(task.powerTarget)}\n${formatDateTime(task.startTime)} → ${formatDateTime(task.endTime)}`}</title>
          </g>
        )
      })}

      {/* "Now" indicator */}
      {showNow && (
        <g>
          <line
            x1={nowX}
            x2={nowX}
            y1={PAD.top}
            y2={PAD.top + PH}
            stroke="hsl(var(--foreground))"
            strokeWidth={1.5}
            strokeDasharray="4 3"
            opacity={0.5}
          />
          <text
            x={nowX}
            y={PAD.top - 4}
            textAnchor="middle"
            fontSize={10}
            fill="currentColor"
            className="text-muted-foreground"
          >
            now
          </text>
        </g>
      )}

      {/* Axis border */}
      <rect
        x={PAD.left}
        y={PAD.top}
        width={PW}
        height={PH}
        fill="none"
        stroke="currentColor"
        className="text-muted-foreground/40"
        strokeWidth={1}
      />

      {/* X-axis labels */}
      {xTicks.map(({ ms, x }, i) => (
        <text
          key={i}
          x={x}
          y={PAD.top + PH + 16}
          textAnchor="middle"
          fontSize={11}
          fill="currentColor"
          className="text-muted-foreground"
        >
          {formatTickTime(ms, tSpan)}
        </text>
      ))}

      {/* Y-axis labels */}
      {yTicks.map(({ p, y }, i) => (
        <text
          key={i}
          x={PAD.left - 8}
          y={y + 4}
          textAnchor="end"
          fontSize={11}
          fill="currentColor"
          className="text-muted-foreground"
        >
          {formatTickPower(p)}
        </text>
      ))}
    </svg>
  )
}

export default function SignalTimeline({
  poolID,
  poolName,
  onSignalsUpdated,
}: SignalTimelineProps) {
  const { data: tasks = [], isLoading: loading, isError, error } = useControlSignals(poolID)
  const deleteSignal = useDeleteSignal()
  const clearAllSignals = useClearAllSignals()
  const incognito = useIncognito()
  const { can } = usePermissions()
  const [selectedTask, setSelectedTask] = useState<string | null>(null)
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [showClearAllDialog, setShowClearAllDialog] = useState(false)
  const [clearAllConfirmText, setClearAllConfirmText] = useState('')
  const { toast } = useToast()

  const isNotControllable =
    isError &&
    ((error as any)?.message?.includes('not controllable') ||
      (error as any)?.message?.includes('403'))

  const handleDeleteClick = (taskID: string) => {
    setTaskToDelete(taskID)
    setDeleteConfirmText('')
  }

  const handleDeleteDialogClose = () => {
    setTaskToDelete(null)
    setDeleteConfirmText('')
  }

  const handleDeleteConfirm = () => {
    if (!taskToDelete) return
    if (deleteConfirmText !== 'DELETE') return

    deleteSignal.mutate(
      { poolID, taskID: taskToDelete },
      {
        onSuccess: () => {
          toast({ title: 'Success', description: 'Control task deleted' })
          handleDeleteDialogClose()
          setSelectedTask(null)
          onSignalsUpdated?.()
        },
        onError: (err: any) => {
          toast({
            title: 'Error',
            description: err.message || 'Failed to delete control task',
            variant: 'destructive',
          })
        },
      }
    )
  }

  const handleClearAllClick = () => {
    setShowClearAllDialog(true)
    setClearAllConfirmText('')
  }

  const handleClearAllDialogClose = () => {
    setShowClearAllDialog(false)
    setClearAllConfirmText('')
  }

  const handleClearAllConfirm = () => {
    if (clearAllConfirmText !== 'CLEAR ALL') return

    clearAllSignals.mutate(
      { poolID },
      {
        onSuccess: () => {
          toast({ title: 'Success', description: 'All control tasks deleted' })
          handleClearAllDialogClose()
          setSelectedTask(null)
          onSignalsUpdated?.()
        },
        onError: (err: any) => {
          toast({
            title: 'Error',
            description: err.message || 'Failed to delete control tasks',
            variant: 'destructive',
          })
        },
      }
    )
  }

  const deleting = deleteSignal.isPending || clearAllSignals.isPending

  if (loading && tasks.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Active Control Tasks</CardTitle>
            <CardDescription>
              Scheduled power setpoints for {poolName} ({tasks.length} task
              {tasks.length !== 1 ? 's' : ''})
            </CardDescription>
          </div>
          {tasks.length > 0 && (
            <AuthorizedButton authorized={can('PoolsControl')} variant="destructive" size="sm" onClick={handleClearAllClick}>
              <Trash2 className="mr-2 h-4 w-4" />
              Clear All
            </AuthorizedButton>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isNotControllable ? (
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-amber-500 opacity-50" />
            <p className="font-medium">Pool Not Controllable</p>
            <p className="text-sm text-muted-foreground mt-2">
              This pool is not configured for control operations
            </p>
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No active control tasks</p>
            <p className="text-sm mt-2">Submit control signals below to get started</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Time-horizon chart */}
            <div className="rounded-lg border bg-card p-2">
              <TaskChart
                tasks={tasks as ScheduledTask[]}
                selectedTask={selectedTask}
                onSelectTask={setSelectedTask}
              />
            </div>

            {/* Compact task rows */}
            <div className="space-y-1.5">
              {[...(tasks as ScheduledTask[])]
                .sort((a, b) => Date.parse(a.startTime) - Date.parse(b.startTime))
                .map((task) => {
                  const isSelected = selectedTask === task.taskID
                  return (
                    <div
                      key={task.taskID}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-muted border-primary/60'
                          : 'hover:bg-muted/50 border-transparent'
                      }`}
                      onClick={() => setSelectedTask(isSelected ? null : task.taskID)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Zap className="h-3.5 w-3.5 text-yellow-500 shrink-0" />
                        <span className="font-semibold tabular-nums">
                          {formatPower(task.powerTarget)}
                        </span>
                        <span className="text-muted-foreground truncate">
                          {formatDateTime(task.startTime)} → {formatDateTime(task.endTime)}
                        </span>
                        <span className="text-xs text-muted-foreground font-mono hidden lg:block truncate">
                          {incognito.uuid(task.taskID)}
                        </span>
                      </div>
                      <AuthorizedButton
                        authorized={can('PoolsControl')}
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteClick(task.taskID)
                        }}
                        className="text-destructive hover:text-destructive ml-2 shrink-0 h-7 w-7 p-0"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </AuthorizedButton>
                    </div>
                  )
                })}
            </div>
          </div>
        )}
      </CardContent>

      {/* Delete Single Task Confirmation Dialog */}
      <AlertDialog open={!!taskToDelete} onOpenChange={(open) => !open && handleDeleteDialogClose()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Control Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this control task?
              <br />
              <br />
              This action cannot be undone. The scheduled power setpoint will be removed.
              <br />
              <br />
              Type <strong>DELETE</strong> to confirm:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-6">
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE"
              disabled={deleting}
              autoFocus
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting || deleteConfirmText !== 'DELETE'}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete Task
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear All Tasks Confirmation Dialog */}
      <AlertDialog
        open={showClearAllDialog}
        onOpenChange={(open) => !open && handleClearAllDialogClose()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear All Control Tasks</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <strong>
                ALL {tasks.length} control task{tasks.length !== 1 ? 's' : ''}
              </strong>{' '}
              for {poolName}?
              <br />
              <br />
              This action cannot be undone. All scheduled power setpoints will be permanently
              removed.
              <br />
              <br />
              Type <strong>CLEAR ALL</strong> to confirm:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-6">
            <Input
              value={clearAllConfirmText}
              onChange={(e) => setClearAllConfirmText(e.target.value)}
              placeholder="Type CLEAR ALL"
              disabled={deleting}
              autoFocus
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearAllConfirm}
              disabled={deleting || clearAllConfirmText !== 'CLEAR ALL'}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Clear All Tasks
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
